import { PageDataType } from '@common/paged-data/enums/page-data-type.enum';
import { PageFilterType } from '@common/paged-data/enums/page-filter-type.enum';
import { PageFilterGroup } from '@common/paged-data/types/page-filter-group.type';
import { LogEventPayload, LogPayload } from '@portal-core/live-log/services/log-stream.service';
import { ProcessLogMessageType } from '@portal-core/processes/enums/process-log-message-type.enum';
import { LogStreamRow } from '@portal-core/processes/models/log-stream-row.model';
import { GridFilterName } from '@portal-core/ui/grid/enums/grid-filter-name.enum';

// Column name shared between every live-log-fed grid's column config and the filter-extraction /
// seed logic. Lives here so the consuming grids and util helpers agree on the same string.
export const TYPE_COLUMN_NAME = 'LogMessageType';

// Translate a `k="e"` (process state event) envelope payload into a `LogStreamRow`.
// Events without a textual message (setProgress, incProgress, …) carry no surface-level info
// and are dropped — same rule the live-log uses, so the two surfaces stay consistent.
export function parseEventToLogStreamRow(payload: LogEventPayload): LogStreamRow | null {
  const message = (payload.msg ?? '').trim();
  if (!message) {
    return null;
  }
  return {
    ErrorId: null,
    File: null,
    Id: null,
    LineIndex: null,
    LogMessageType: ProcessLogMessageType.Event,
    LongMessage: message,
    Message: message,
    StartTime: payload.t ?? null,
    Subsystem: null
  } as LogStreamRow;
}

// Translate a `k="l"` envelope payload into a `LogStreamRow` for the grid. Returns null for
// envelopes the grid shouldn't display.
export function parseLogToLogStreamRow(payload: LogPayload): LogStreamRow | null {
  // madbuild emits Windows-style backslash paths on Windows agents; normalize so the displayed
  // file column matches the rest of the portal's path conventions.
  const file = payload.src ? payload.src.replace(/\\/g, '/') : null;

  return {
    ErrorId: payload.errorId ?? null,
    File: file,
    Id: null,
    LineIndex: typeof payload.srcLine === 'number' ? payload.srcLine : null,
    LogMessageType: severityToLogMessageType(payload.sev),
    LongMessage: payload.msg ?? '',
    Message: payload.msg ?? '',
    StartTime: payload.t ?? null,
    Subsystem: payload.subsystem ?? null
  } as LogStreamRow;
}

// Map a wire-level severity string onto the ProcessLogMessageType enum used by the grid.
// Unknown values (including "debug") fall through to `Message` so the row still renders
// — debug entries aren't generated in production today, but if they ever are they should land
// in the "Info" filter bucket rather than disappear.
export function severityToLogMessageType(sev: string | undefined): ProcessLogMessageType {
  switch (sev) {
    case 'error': return ProcessLogMessageType.Error;
    case 'warn': return ProcessLogMessageType.Warning;
    case 'info': return ProcessLogMessageType.Message;
    default: return ProcessLogMessageType.Message;
  }
}

// Walk an outer `grid-selects` group (or top-level PageFilter that contains one) down to the
// `LogMessageType` sub-group and return its raw `PropertyValue` ids as a string[]. Returns []
// when no Type filter is active, which the caller treats as "show everything."
export function extractTypeFilterValuesFromGroup(group: { FilterGroups?: PageFilterGroup[] } | undefined): string[] {
  const selectGroup = group?.FilterGroups?.find(g => g.Id === GridFilterName.Select);
  const typeGroup = selectGroup?.FilterGroups?.find(g => g.Id === TYPE_COLUMN_NAME);
  return typeGroup?.Filters?.map(f => String(f.PropertyValue)) ?? [];
}

// Same extraction, but applied to the dictionary shape that NGXS state stores under
// `grids[gridId].config.filters` — keys are the named filter group ids ('grid-selects', etc.)
// rather than the array form used inside a merged PageFilter.
export function extractTypeFilterValues(filters: Dictionary<PageFilterGroup> | undefined): string[] {
  const selectGroup = filters?.[GridFilterName.Select];
  const typeGroup = selectGroup?.FilterGroups?.find(g => g.Id === TYPE_COLUMN_NAME);
  return typeGroup?.Filters?.map(f => String(f.PropertyValue)) ?? [];
}

// Walk the merged PageFilter down to the `grid-strings` group and return a Map of
// columnName → lowercased search term for every active string filter. Returns an empty map
// when no string filters are active, which the caller treats as "no restriction."
export function extractStringFilterValues(group: { FilterGroups?: PageFilterGroup[] } | undefined): Map<string, string> {
  const result = new Map<string, string>();
  const stringGroup = group?.FilterGroups?.find(g => g.Id === GridFilterName.String);
  if (!stringGroup?.FilterGroups) return result;
  for (const colGroup of stringGroup.FilterGroups) {
    const raw = colGroup.Filters?.[0]?.PropertyValue;
    if (raw != null && String(raw).length > 0) {
      result.set(colGroup.Id, String(raw).toLowerCase());
    }
  }
  return result;
}

// Build a single Equals filter for one Type column value, used when seeding the grid filter
// state from a stored array of selected ids.
export function makeTypeFilter(value: string) {
  return {
    FilterType: PageFilterType.Equals,
    PropertyName: TYPE_COLUMN_NAME,
    PropertyType: PageDataType.Select,
    PropertyValue: value
  };
}

// Project-titan icon name for each ProcessLogMessageType, used by the Type column cell template.
export function logMessageTypeIcon(logMessageType: ProcessLogMessageType): string {
  switch (logMessageType) {
    case ProcessLogMessageType.Error: return 'icon-error';
    case ProcessLogMessageType.Warning: return 'icon-alert';
    case ProcessLogMessageType.Message: return 'icon-info-outline';
    case ProcessLogMessageType.Event: return 'icon-pennant-outline';
    default: return '';
  }
}

// CSS modifier class for the Type column icon's color, so each type tints to match the
// equivalent log-line color in the live-log surface (red/yellow/muted). Returns empty for
// Info because it's intentionally left the default icon color — Info is the most common log
// type and tinting it would compete with Error/Warning for the user's attention.
export function logMessageTypeColorClass(logMessageType: ProcessLogMessageType): string {
  switch (logMessageType) {
    case ProcessLogMessageType.Error:   return 'mc-log-stream-grid-type-icon--error';
    case ProcessLogMessageType.Warning: return 'mc-log-stream-grid-type-icon--warn';
    case ProcessLogMessageType.Event:   return 'mc-log-stream-grid-type-icon--event';
    default: return '';
  }
}
